import { Fragment, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import {
  FREIGHT_MODE_LABELS,
  FREIGHT_MODES,
  MEASURE_UNIT_LABELS,
  MEASURE_UNITS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  PURCHASE_ORDER_AVULSO_REASON_LABELS,
  PURCHASE_ORDER_AVULSO_REASONS,
  formatCents,
  formatQty,
  type MeasureUnit,
  type Paginated,
  type PurchaseOrder,
  type Quotation,
  type QuotationPendingOrders,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { formatDocument } from '@/lib/format';
import { T } from '@/design/tokens';
import { Button } from '@/components/Button';
import { Drawer } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput, QtyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useCostCenters, useItems, useStockLocations, useSuppliers, useUnits } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';
import { StatusBadge } from '@/components/StatusBadge';

/* ------------------------ condições comerciais ---------------------------- */

export interface Comercial {
  costCenterId: string;
  unitId: string;
  locationId: string;
  deliveryAddress: string;
  deliveryDays: string;
  paymentTerms: string;
  paymentMethod: string;
  freightCents: number | null;
  freightMode: string;
  carrierName: string;
  freightNotes: string;
  discountCents: number | null;
  expectedAt: string;
  supplierInstructions: string;
  notes: string;
}

export const comercialVazio = (): Comercial => ({
  costCenterId: '',
  unitId: '',
  locationId: '',
  deliveryAddress: '',
  deliveryDays: '',
  paymentTerms: '',
  paymentMethod: '',
  freightCents: null,
  freightMode: '',
  carrierName: '',
  freightNotes: '',
  discountCents: null,
  expectedAt: '',
  supplierInstructions: '',
  notes: '',
});

/** O que vai no corpo da requisição HTTP — vazio vira `null`, nunca `''`. */
export function comercialBody(c: Comercial) {
  return {
    costCenterId: c.costCenterId || undefined,
    unitId: c.unitId || null,
    locationId: c.locationId || null,
    deliveryAddress: c.deliveryAddress.trim() || null,
    deliveryDays: c.deliveryDays ? Number(c.deliveryDays) : null,
    paymentTerms: c.paymentTerms.trim() || null,
    paymentMethod: c.paymentMethod || null,
    freightCents: c.freightCents ?? 0,
    freightMode: c.freightMode || null,
    carrierName: c.carrierName.trim() || null,
    freightNotes: c.freightNotes.trim() || null,
    discountCents: c.discountCents ?? 0,
    expectedAt: c.expectedAt || null,
    supplierInstructions: c.supplierInstructions.trim() || null,
    notes: c.notes.trim() || null,
  };
}

/** Os blocos comerciais, para quem quiser só um pedaço deles. */
type BlocoComercial = 'destino' | 'condicoes' | 'frete' | 'observacoes';
const TODOS_OS_BLOCOS: BlocoComercial[] = ['destino', 'condicoes', 'frete', 'observacoes'];

function CamposComerciais({
  value,
  onChange,
  erros,
  exigirCentroDeCusto,
  blocos = TODOS_OS_BLOCOS,
}: {
  value: Comercial;
  onChange: (patch: Partial<Comercial>) => void;
  erros: Record<string, string>;
  exigirCentroDeCusto: boolean;
  /**
   * Quais blocos desenhar. O padrão é todos, que é como os três formulários
   * de pedido sempre usaram — o passo a passo do avulso é quem pede um de
   * cada vez. Um parâmetro com padrão em vez de quatro componentes novos:
   * os campos continuam num lugar só, e não há duas versões para divergir.
   */
  blocos?: BlocoComercial[];
}) {
  const mostrar = (bloco: BlocoComercial) => blocos.includes(bloco);
  const costCenters = useCostCenters();
  const locations = useStockLocations();
  const units = useUnits();
  const [freteAberto, setFreteAberto] = useState(
    Boolean(value.freightCents || value.freightMode || value.carrierName),
  );

  return (
    <>
      {mostrar('destino') && (
      <FormSection title="Destino e centro de custo">
        <Field htmlFor="pc-costCenterId" label={`Centro de custo${exigirCentroDeCusto ? '' : ' (herda da requisição)'}`} error={erros.costCenterId}>
          <select className="input" id="pc-costCenterId" value={value.costCenterId} onChange={(e) => onChange({ costCenterId: e.target.value })}>
            <option value="">{exigirCentroDeCusto ? 'Escolha a etapa' : 'Como na requisição'}</option>
            {(costCenters.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field htmlFor="pc-locationId" label="Local de entrega" hint="Onde o material será recebido — não confundir com centro de custo">
          <select className="input" id="pc-locationId" value={value.locationId} onChange={(e) => onChange({ locationId: e.target.value })}>
            <option value="">Não definido</option>
            {(locations.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.code} · {l.name}
              </option>
            ))}
          </select>
        </Field>
        <Field htmlFor="pc-unitId" label="Unidade">
          <select className="input" id="pc-unitId" value={value.unitId} onChange={(e) => onChange({ unitId: e.target.value })}>
            <option value="">Não definida</option>
            {(units.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </Field>
        <Field htmlFor="pc-deliveryAddress" label="Endereço de entrega" hint="Só quando for diferente do endereço do local">
          <input className="input" id="pc-deliveryAddress" value={value.deliveryAddress} onChange={(e) => onChange({ deliveryAddress: e.target.value })} />
        </Field>
      </FormSection>
      )}

      {mostrar('condicoes') && (
      <FormSection title="Condições comerciais">
        <Field htmlFor="pc-paymentTerms" label="Condição de pagamento" hint="À vista, 30 dias, 30/60/90, entrada + parcelas…">
          <input className="input" id="pc-paymentTerms"
            value={value.paymentTerms}
            onChange={(e) => onChange({ paymentTerms: e.target.value })}
            placeholder="30/60/90"
          />
        </Field>
        <Field htmlFor="pc-paymentMethod" label="Forma prevista">
          <select className="input" id="pc-paymentMethod" value={value.paymentMethod} onChange={(e) => onChange({ paymentMethod: e.target.value })}>
            <option value="">Não definida</option>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field htmlFor="pc-deliveryDays" label="Prazo de entrega (dias)">
          <input className="input" id="pc-deliveryDays"
            type="number"
            min={0}
            max={365}
            value={value.deliveryDays}
            onChange={(e) => onChange({ deliveryDays: e.target.value })}
          />
        </Field>
        <Field label="Data prevista de entrega" error={erros.expectedAt}>
          <DateInput className="" value={value.expectedAt} onChange={(iso) => onChange({ expectedAt: iso })} />
        </Field>
        <Field label="Desconto">
          <MoneyInput value={value.discountCents} onChange={(cents) => onChange({ discountCents: cents })} />
        </Field>
      </FormSection>
      )}

      {/* O frete não abre por padrão: na maioria dos pedidos ele está no
          preço, e quatro campos a mais só deixam a tela intimidadora. */}
      {mostrar('frete') && (
      <FormSection title="Frete">
        {!freteAberto ? (
          <button type="button" className="btn btn--ghost" onClick={() => setFreteAberto(true)}>
            Informar frete
          </button>
        ) : (
          <>
            <Field htmlFor="pc-freightMode" label="Quem paga">
              <select className="input" id="pc-freightMode" value={value.freightMode} onChange={(e) => onChange({ freightMode: e.target.value })}>
                <option value="">Não definido</option>
                {FREIGHT_MODES.map((m) => (
                  <option key={m} value={m}>
                    {FREIGHT_MODE_LABELS[m]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Valor do frete" hint="Entra rateado no custo de entrada do estoque">
              <MoneyInput value={value.freightCents} onChange={(cents) => onChange({ freightCents: cents })} />
            </Field>
            <Field htmlFor="pc-carrierName" label="Transportadora">
              <input className="input" id="pc-carrierName" value={value.carrierName} onChange={(e) => onChange({ carrierName: e.target.value })} />
            </Field>
            <Field htmlFor="pc-freightNotes" label="Observações do frete">
              <input className="input" id="pc-freightNotes" value={value.freightNotes} onChange={(e) => onChange({ freightNotes: e.target.value })} />
            </Field>
          </>
        )}
      </FormSection>
      )}

      {mostrar('observacoes') && (
      <FormSection title="Observações">
        <Field htmlFor="pc-supplierInstructions" label="Instruções ao fornecedor" hint="Vai impresso no pedido">
          <textarea className="input" id="pc-supplierInstructions"
            rows={2}
            value={value.supplierInstructions}
            onChange={(e) => onChange({ supplierInstructions: e.target.value })}
          />
        </Field>
        <Field htmlFor="pc-notes" label="Observações internas">
          <textarea className="input" id="pc-notes" rows={2} value={value.notes} onChange={(e) => onChange({ notes: e.target.value })} />
        </Field>
      </FormSection>
      )}
    </>
  );
}

/* --------------------------- itens do pedido ------------------------------ */

export interface LinhaDraft {
  key: string;
  itemId: string;
  description: string;
  specification: string;
  brand: string;
  model: string;
  unit: MeasureUnit;
  quantityMilli: number | null;
  unitPriceCents: number | null;
  discountCents: number | null;
}

export const linhaVazia = (): LinhaDraft => ({
  key: crypto.randomUUID(),
  itemId: '',
  description: '',
  specification: '',
  brand: '',
  model: '',
  unit: 'UN',
  quantityMilli: null,
  unitPriceCents: null,
  discountCents: null,
});

function totalDaLinha(linha: LinhaDraft) {
  const bruto = Math.round(((linha.quantityMilli ?? 0) / 1000) * (linha.unitPriceCents ?? 0));
  return Math.max(bruto - (linha.discountCents ?? 0), 0);
}

function Itens({
  linhas,
  onChange,
  bloqueadas,
}: {
  linhas: LinhaDraft[];
  onChange: (linhas: LinhaDraft[]) => void;
  /** Linhas com material já recebido: quantidade não desce abaixo do recebido. */
  bloqueadas?: Record<string, number>;
}) {
  const items = useItems();

  const patch = (key: string, dados: Partial<LinhaDraft>) =>
    onChange(linhas.map((l) => (l.key === key ? { ...l, ...dados } : l)));

  return (
    <FormSection title="Itens">
      {linhas.map((linha) => (
        <div
          key={linha.key}
          style={{
            border: `1px solid ${T.line}`,
            borderRadius: 10,
            padding: 12,
            marginBottom: 10,
            display: 'grid',
            gap: 8,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end' }}>
            <Field label="Item do estoque" hint="Deixe vazio para material não cadastrado">
              <select className="input"
                value={linha.itemId}
                onChange={(e) => {
                  const escolhido = (items.data ?? []).find((i) => i.id === e.target.value);
                  patch(linha.key, {
                    itemId: e.target.value,
                    description: escolhido ? escolhido.name : linha.description,
                    unit: escolhido ? escolhido.unit : linha.unit,
                  });
                }}
              >
                <option value="">Não cadastrado</option>
                {(items.data ?? []).map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.sku} · {i.name}
                  </option>
                ))}
              </select>
            </Field>
            {linhas.length > 1 && (
              <button
                type="button"
                className="btn btn--ghost"
                aria-label="Remover item"
                onClick={() => onChange(linhas.filter((l) => l.key !== linha.key))}
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>

          <Field label="Descrição">
            <input className="input" value={linha.description} onChange={(e) => patch(linha.key, { description: e.target.value })} />
          </Field>
          <Field label="Especificação" hint="É o que o recebimento vai conferir contra o que chegar">
            <input className="input" value={linha.specification} onChange={(e) => patch(linha.key, { specification: e.target.value })} />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Marca">
              <input className="input" value={linha.brand} onChange={(e) => patch(linha.key, { brand: e.target.value })} />
            </Field>
            <Field label="Modelo">
              <input className="input" value={linha.model} onChange={(e) => patch(linha.key, { model: e.target.value })} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
            <Field label="Unidade">
              <select className="input"
                value={linha.unit}
                onChange={(e) => patch(linha.key, { unit: e.target.value as MeasureUnit })}
              >
                {MEASURE_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {MEASURE_UNIT_LABELS[u]}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Quantidade"
              hint={
                bloqueadas?.[linha.description]
                  ? `mínimo ${(bloqueadas[linha.description]! / 1000).toLocaleString('pt-BR')} (já recebido)`
                  : undefined
              }
            >
              <QtyInput
                value={linha.quantityMilli}
                unit={linha.unit}
                onChange={(milli) => patch(linha.key, { quantityMilli: milli })}
              />
            </Field>
            <Field label="Preço unitário">
              <MoneyInput
                value={linha.unitPriceCents}
                onChange={(cents) => patch(linha.key, { unitPriceCents: cents })}
              />
            </Field>
            <Field label="Desconto">
              <MoneyInput
                value={linha.discountCents}
                onChange={(cents) => patch(linha.key, { discountCents: cents })}
              />
            </Field>
          </div>

          <div style={{ textAlign: 'right', fontSize: 12, color: T.inkSoft }}>
            Total da linha: <strong>{formatCents(totalDaLinha(linha))}</strong>
          </div>
        </div>
      ))}

      <Button variant="ghost" icon={Plus} onClick={() => onChange([...linhas, linhaVazia()])}>
        Adicionar item
      </Button>
    </FormSection>
  );
}

const itensBody = (linhas: LinhaDraft[]) =>
  linhas.map((l) => ({
    itemId: l.itemId || null,
    description: l.description.trim(),
    specification: l.specification.trim() || null,
    brand: l.brand.trim() || null,
    model: l.model.trim() || null,
    unit: l.unit,
    quantityMilli: l.quantityMilli ?? 0,
    unitPriceCents: l.unitPriceCents ?? 0,
    discountCents: l.discountCents ?? 0,
  }));

/* -------------------------- pedido de cotação ----------------------------- */

/**
 * Emitir pedido a partir da cotação.
 *
 * Escolhe a cotação finalizada, e o painel mostra UM BLOCO POR FORNECEDOR
 * vencedor, com o que cada um levou. Fornecedor que já tem pedido aparece
 * marcado — é o que impede a mesma quantidade ser pedida duas vezes.
 */
export function DaCotacaoDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (order: PurchaseOrder) => void;
}) {
  const [quotationId, setQuotationId] = useState('');
  const [comercial, setComercial] = useState<Comercial>(comercialVazio());
  const [erro, setErro] = useState<string | null>(null);

  const cotacoes = useQuery({
    queryKey: ['quotations', 'finalizadas'],
    queryFn: () =>
      api.get<Paginated<Quotation>>('/quotations', { status: 'FINALIZADA', perPage: 100 }),
  });

  const pendentes = useQuery({
    queryKey: ['quotation', quotationId, 'pending-orders'],
    queryFn: () => api.get<QuotationPendingOrders>(`/quotations/${quotationId}/pending-orders`),
    enabled: Boolean(quotationId),
  });

  const emitir = useMutation({
    mutationFn: (personId: string) =>
      api.post<PurchaseOrder>('/purchase-orders/from-quotation', {
        quotationId,
        personId,
        ...comercialBody(comercial),
      }),
    onSuccess: onCreated,
    onError: (e) => setErro(errorMessage(e)),
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Pedido a partir de cotação"
      description="Itens, quantidades e preços vêm da proposta vencedora — sem redigitar."
      footer={
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <FormSection title="Cotação">
        <Field label="Cotação finalizada">
          <select className="input"
            value={quotationId}
            onChange={(e) => {
              setQuotationId(e.target.value);
              setErro(null);
            }}
          >
            <option value="">Escolha a cotação</option>
            {(cotacoes.data?.data ?? []).map((q) => (
              <option key={q.id} value={q.id}>
                {q.code} · {q.requisitionCode}
              </option>
            ))}
          </select>
        </Field>
      </FormSection>

      {quotationId && pendentes.isPending && <LoadingState label="Lendo a cotação…" />}
      {pendentes.isError && <ErrorState message={errorMessage(pendentes.error)} />}

      {pendentes.data && (
        <>
          <CamposComerciais
            value={comercial}
            onChange={(patch) => setComercial((c) => ({ ...c, ...patch }))}
            erros={{}}
            exigirCentroDeCusto={false}
          />

          <FormSection
            title="Fornecedores vencedores"
            description="Cada fornecedor gera o próprio pedido. Misturar dois no mesmo documento não é pedido que se mande para ninguém."
          >
            {pendentes.data.suppliers.length === 0 && (
              <EmptyState
                title="Nenhum vencedor definido"
                description="Escolha os vencedores da cotação antes de emitir o pedido."
              />
            )}
            {pendentes.data.suppliers.map((forn) => (
              <div
                key={forn.personId}
                style={{
                  border: `1px solid ${T.line}`,
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 8,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{forn.supplierName}</div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {forn.supplierDocument ? formatDocument(forn.supplierDocument) : '—'} · {forn.itemCount}{' '}
                    {forn.itemCount === 1 ? 'item' : 'itens'} · {formatCents(forn.totalCents)}
                  </div>
                  {forn.alreadyOrdered && (
                    <div style={{ fontSize: 11, color: T.green, marginTop: 2 }}>
                      já gerou {forn.orderCodes.join(', ')}
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => emitir.mutate(forn.personId)}
                  disabled={forn.alreadyOrdered || emitir.isPending}
                >
                  Emitir pedido
                </Button>
              </div>
            ))}
          </FormSection>
        </>
      )}

      {erro && <p style={{ color: T.red, fontSize: 12 }}>{erro}</p>}
    </Drawer>
  );
}

/* ---------------------------- pedido avulso ------------------------------- */

/**
 * Pedido avulso.
 *
 * Existe porque a compra de emergência existe. Motivo e justificativa são
 * obrigatórios, a permissão é própria (`pedidos-compra:avulso`) e ele passa
 * pela mesma aprovação dos outros — o avulso encurta o caminho da cotação,
 * não o de quem autoriza.
 */
/**
 * Os passos do avulso.
 *
 * Cada um existe porque há CAMPO por trás dele. O reboot pedia também um
 * passo "Anexos", e ele não entrou: anexo se prende a um pedido, e aqui o
 * pedido ainda não existe. Um passo que só guarda arquivo na memória do
 * navegador até o fim seria promessa de guardar o que ainda não dá — os
 * anexos continuam na tela do pedido, depois de criado.
 */
const PASSOS_DO_AVULSO = [
  { chave: 'motivo', titulo: 'Motivo', pergunta: 'Por que esta compra não pode esperar a requisição?' },
  { chave: 'fornecedor', titulo: 'Fornecedor', pergunta: 'De quem vamos comprar' },
  { chave: 'destino', titulo: 'Destino', pergunta: 'Qual etapa paga e onde o material chega' },
  { chave: 'itens', titulo: 'Itens', pergunta: 'O que entra no pedido' },
  { chave: 'condicoes', titulo: 'Condições', pergunta: 'Pagamento, prazo e desconto' },
  { chave: 'frete', titulo: 'Frete', pergunta: 'Quem paga o transporte, quando houver' },
  { chave: 'revisao', titulo: 'Revisão', pergunta: 'Confira antes de criar o rascunho' },
] as const;

/** Valor que ainda não foi preenchido aparece como travessão, não em branco. */
function ouTraco(valor: string | null | undefined) {
  return valor && valor.trim() ? valor : <span className="resumo__vazio">—</span>;
}

/**
 * Pedido avulso.
 *
 * Existe porque a compra de emergência existe. Motivo e justificativa são
 * obrigatórios, a permissão é própria (`pedidos-compra:avulso`) e ele passa
 * pela mesma aprovação dos outros — o avulso encurta o caminho da cotação,
 * não o de quem autoriza.
 *
 * Era uma rolagem só com sete blocos empilhados: fornecedor no alto, itens
 * lá embaixo, e o total só aparecia no fim. Agora é passo a passo, com o
 * RESUMO fixo ao lado — quem monta vê o que está montando sem voltar.
 */
export function AvulsoDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (order: PurchaseOrder) => void;
}) {
  const suppliers = useSuppliers();
  const costCenters = useCostCenters();
  const [personId, setPersonId] = useState('');
  const [avulsoReason, setAvulsoReason] = useState<string>('EMERGENCIAL');
  const [justificativa, setJustificativa] = useState('');
  const [comercial, setComercial] = useState<Comercial>(comercialVazio());
  const [linhas, setLinhas] = useState<LinhaDraft[]>([linhaVazia()]);
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [passo, setPasso] = useState(0);

  const criar = useMutation({
    mutationFn: () =>
      api.post<PurchaseOrder>('/purchase-orders', {
        personId,
        avulsoReason,
        avulsoJustification: justificativa.trim() || null,
        ...comercialBody(comercial),
        items: itensBody(linhas),
      }),
    onSuccess: onCreated,
    onError: (e) => {
      setErro(errorMessage(e));
      setErros(fieldErrors(e));
      // O erro do servidor pode ser de QUALQUER passo. Voltar para a revisão
      // deixa o recado à vista em vez de escondê-lo atrás do passo atual.
      setPasso(PASSOS_DO_AVULSO.length - 1);
    },
  });

  const totalItens = linhas.reduce((soma, linha) => soma + totalDaLinha(linha), 0);
  const total = totalItens + (comercial.freightCents ?? 0) - (comercial.discountCents ?? 0);
  const precisaJustificar = avulsoReason === 'OUTRO';
  const linhasValidas = linhas.filter(
    (linha) => linha.description.trim().length >= 2 && (linha.quantityMilli ?? 0) > 0,
  );

  const fornecedor = (suppliers.data ?? []).find((s) => s.id === personId);
  const centroDeCusto = (costCenters.data ?? []).find((c) => c.id === comercial.costCenterId);

  /**
   * O que falta em cada passo.
   *
   * É a MESMA lista que libera o "Continuar" e que trava o "Criar rascunho":
   * dois critérios diferentes deixariam o botão final aceso com passo
   * incompleto atrás.
   */
  const pendencias: Record<string, string | null> = {
    motivo:
      precisaJustificar && justificativa.trim().length < 10
        ? 'Em "Outro", a justificativa precisa de ao menos 10 caracteres'
        : null,
    fornecedor: personId ? null : 'Escolha o fornecedor',
    destino: comercial.costCenterId ? null : 'Escolha o centro de custo que paga',
    itens:
      linhasValidas.length > 0 && linhasValidas.length === linhas.length
        ? null
        : 'Toda linha precisa de descrição e quantidade',
    condicoes: null,
    frete: null,
    revisao: null,
  };

  const chaveAtual = PASSOS_DO_AVULSO[passo]!.chave;
  const pendenciaAtual = pendencias[chaveAtual] ?? null;
  const primeiraPendencia = PASSOS_DO_AVULSO.findIndex((p) => pendencias[p.chave]);
  const podeCriar = primeiraPendencia === -1;
  const ultimo = passo === PASSOS_DO_AVULSO.length - 1;

  return (
    <Drawer
      open
      onClose={onClose}
      title="Pedido avulso"
      description="Compra fora do fluxo requisição → cotação → aprovação. Fica marcada como tal."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <span className="muted" style={{ fontSize: 12.5, marginRight: 'auto' }}>
            Passo {passo + 1} de {PASSOS_DO_AVULSO.length} · {PASSOS_DO_AVULSO[passo]!.titulo}
          </span>
          <Button variant="outline" disabled={passo === 0} onClick={() => setPasso((p) => p - 1)}>
            Voltar
          </Button>
          {ultimo ? (
            <Button
              variant="primary"
              onClick={() => criar.mutate()}
              disabled={!podeCriar || criar.isPending}
              loading={criar.isPending}
            >
              Criar rascunho
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={Boolean(pendenciaAtual)}
              onClick={() => setPasso((p) => p + 1)}
            >
              Continuar
            </Button>
          )}
        </>
      }
    >
      {/* `nav` com botões, e não lista: cada passo É um controle que leva a
          algum lugar. Marcá-los como item de lista deixava o papel de botão
          escondido de quem navega por leitor de tela. */}
      <nav className="passos" aria-label="Etapas do pedido avulso">
        {PASSOS_DO_AVULSO.map((item, indice) => (
          <Fragment key={item.chave}>
            {indice > 0 && <span className="passo__traco" aria-hidden />}
            <button
              type="button"
              className={`passo${indice === passo ? ' is-atual' : indice < passo ? ' is-feito' : ''}`}
              aria-current={indice === passo ? 'step' : undefined}
              // Só para trás: pular adiante deixaria o resumo mentindo sobre
              // o que já foi decidido.
              disabled={indice > passo}
              onClick={() => setPasso(indice)}
            >
              <span className="passo__n">{indice + 1}</span>
              {item.titulo}
            </button>
          </Fragment>
        ))}
      </nav>

      <div className="montagem">
        <div>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            {PASSOS_DO_AVULSO[passo]!.pergunta}
          </p>

          {chaveAtual === 'motivo' && (
            <>
              <div className="attention__item attention__item--warn" style={{ marginBottom: 14 }}>
                <span className="attention__text">
                  O pedido avulso <strong>não pula a aprovação</strong>: ele nasce em rascunho e
                  segue para quem aprova, como qualquer outro. O que ele dispensa é a cotação — e
                  por isso o motivo fica registrado na auditoria.
                </span>
              </div>
              <FormSection>
                <Field label="Motivo" htmlFor="avulso-motivo" error={erros.avulsoReason}>
                  <select
                    id="avulso-motivo"
                    className="input"
                    value={avulsoReason}
                    onChange={(e) => setAvulsoReason(e.target.value)}
                  >
                    {PURCHASE_ORDER_AVULSO_REASONS.map((m) => (
                      <option key={m} value={m}>
                        {PURCHASE_ORDER_AVULSO_REASON_LABELS[m]}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label={`Justificativa${precisaJustificar ? '' : ' (opcional)'}`}
                  htmlFor="avulso-justificativa"
                  error={erros.avulsoJustification}
                  hint="O aprovador lê isto antes de liberar a compra"
                >
                  <textarea
                    id="avulso-justificativa"
                    className="input"
                    rows={3}
                    value={justificativa}
                    onChange={(e) => setJustificativa(e.target.value)}
                    placeholder="Ex.: bomba da piscina de ondas parou no sábado"
                  />
                </Field>
              </FormSection>
            </>
          )}

          {chaveAtual === 'fornecedor' && (
            <FormSection>
              <Field label="Fornecedor" htmlFor="avulso-fornecedor" error={erros.personId}>
                <select
                  id="avulso-fornecedor"
                  className="input"
                  value={personId}
                  onChange={(e) => setPersonId(e.target.value)}
                >
                  <option value="">Escolha o fornecedor</option>
                  {(suppliers.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.tradeName || s.name}
                    </option>
                  ))}
                </select>
              </Field>
            </FormSection>
          )}

          {chaveAtual === 'destino' && (
            <CamposComerciais
              value={comercial}
              onChange={(patch) => setComercial((c) => ({ ...c, ...patch }))}
              erros={erros}
              exigirCentroDeCusto
              blocos={['destino']}
            />
          )}

          {chaveAtual === 'itens' && <Itens linhas={linhas} onChange={setLinhas} />}

          {chaveAtual === 'condicoes' && (
            <CamposComerciais
              value={comercial}
              onChange={(patch) => setComercial((c) => ({ ...c, ...patch }))}
              erros={erros}
              exigirCentroDeCusto
              blocos={['condicoes']}
            />
          )}

          {chaveAtual === 'frete' && (
            <CamposComerciais
              value={comercial}
              onChange={(patch) => setComercial((c) => ({ ...c, ...patch }))}
              erros={erros}
              exigirCentroDeCusto
              blocos={['frete', 'observacoes']}
            />
          )}

          {chaveAtual === 'revisao' && (
            <>
              {/* O que falta, com o caminho de volta. Listar o problema sem
                  levar até ele obriga o operador a caçar o passo. */}
              {!podeCriar && (
                <div className="attention__item attention__item--warn" style={{ marginBottom: 14 }}>
                  <span className="attention__text">
                    Falta preencher: {pendencias[PASSOS_DO_AVULSO[primeiraPendencia]!.chave]}.{' '}
                    <button
                      type="button"
                      className="purchase-list-heading__clear"
                      onClick={() => setPasso(primeiraPendencia)}
                    >
                      Ir para {PASSOS_DO_AVULSO[primeiraPendencia]!.titulo}
                    </button>
                  </span>
                </div>
              )}
              <FormSection title="Itens do pedido">
                <div className="linhas">
                  <div className="linhas__cab" style={{ gridTemplateColumns: COLUNAS_DA_REVISAO }}>
                    <span>Descrição</span>
                    <span className="linhas__num">Qtde.</span>
                    <span className="linhas__num">Unitário</span>
                    <span className="linhas__num">Total</span>
                  </div>
                  {linhasValidas.map((linha, indice) => (
                    <div
                      key={indice}
                      className="linhas__linha"
                      style={{ gridTemplateColumns: COLUNAS_DA_REVISAO }}
                    >
                      <span>{linha.description}</span>
                      <span className="linhas__total">
                        {formatQty(linha.quantityMilli ?? 0)} {linha.unit}
                      </span>
                      <span className="linhas__total">{formatCents(linha.unitPriceCents ?? 0)}</span>
                      <span className="linhas__total">{formatCents(totalDaLinha(linha))}</span>
                    </div>
                  ))}
                </div>
              </FormSection>
            </>
          )}

          {erro && (
            <div className="login__alert" role="alert" style={{ marginTop: 14 }}>
              {erro}
            </div>
          )}
        </div>

        <dl className="resumo">
          <p className="resumo__titulo">
            Resumo
            <StatusBadge kind="mute">Rascunho</StatusBadge>
          </p>
          <div className="resumo__linha">
            <dt>Motivo</dt>
            <dd>{PURCHASE_ORDER_AVULSO_REASON_LABELS[avulsoReason as never] ?? avulsoReason}</dd>
          </div>
          <div className="resumo__linha">
            <dt>Fornecedor</dt>
            <dd>{ouTraco(fornecedor?.tradeName || fornecedor?.name)}</dd>
          </div>
          <div className="resumo__linha">
            <dt>Centro de custo</dt>
            <dd>{ouTraco(centroDeCusto && `${centroDeCusto.code} · ${centroDeCusto.name}`)}</dd>
          </div>
          <div className="resumo__linha">
            <dt>Itens</dt>
            <dd>{linhasValidas.length}</dd>
          </div>
          <div className="resumo__linha">
            <dt>Subtotal</dt>
            <dd className="mono">{formatCents(totalItens)}</dd>
          </div>
          <div className="resumo__linha">
            <dt>Frete</dt>
            <dd className="mono">{formatCents(comercial.freightCents ?? 0)}</dd>
          </div>
          <div className="resumo__linha">
            <dt>Desconto</dt>
            <dd className="mono">{formatCents(comercial.discountCents ?? 0)}</dd>
          </div>
          <div className="resumo__linha resumo__linha--total">
            <dt>Total</dt>
            <dd>{formatCents(total)}</dd>
          </div>
          <div className="resumo__linha">
            <dt>Entrega</dt>
            <dd>{ouTraco(comercial.expectedAt)}</dd>
          </div>
          <div className="resumo__linha">
            <dt>Pagamento</dt>
            <dd>{ouTraco(comercial.paymentTerms)}</dd>
          </div>
        </dl>
      </div>
    </Drawer>
  );
}

/** Colunas da tabela de conferência do último passo. */
const COLUNAS_DA_REVISAO = 'minmax(0, 1fr) 130px 130px 130px';

/* -------------------------- revisão do pedido ----------------------------- */

/**
 * Alterar pedido já emitido.
 *
 * Não sobrescreve: gera uma REVISÃO numerada com o antes, o depois e o motivo.
 * Se o valor novo estourar a alçada que aprovou, o pedido volta sozinho para
 * aprovação — o aviso está na tela antes de salvar.
 */
export function RevisarDrawer({
  order,
  onClose,
  onSaved,
}: {
  order: PurchaseOrder;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState('');
  const [comercial, setComercial] = useState<Comercial>({
    ...comercialVazio(),
    costCenterId: order.costCenterId,
    unitId: order.unitId ?? '',
    locationId: order.locationId ?? '',
    deliveryAddress: order.deliveryAddress ?? '',
    deliveryDays: order.deliveryDays !== null ? String(order.deliveryDays) : '',
    paymentTerms: order.paymentTerms ?? '',
    paymentMethod: order.paymentMethod ?? '',
    freightCents: order.freightCents || null,
    freightMode: order.freightMode ?? '',
    carrierName: order.carrierName ?? '',
    freightNotes: order.freightNotes ?? '',
    discountCents: order.discountCents || null,
    expectedAt: order.expectedAt ?? '',
    supplierInstructions: order.supplierInstructions ?? '',
    notes: order.notes ?? '',
  });
  const [linhas, setLinhas] = useState<LinhaDraft[]>(
    order.items.map((item) => ({
      key: item.id,
      itemId: item.itemId ?? '',
      description: item.description,
      specification: item.specification ?? '',
      brand: item.brand ?? '',
      model: item.model ?? '',
      unit: item.unit,
      quantityMilli: item.quantityMilli,
      unitPriceCents: item.unitPriceCents,
      discountCents: item.discountCents || null,
    })),
  );
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  const bloqueadas = Object.fromEntries(
    order.items.filter((i) => i.receivedQuantityMilli > 0).map((i) => [i.description, i.receivedQuantityMilli]),
  );

  const salvar = useMutation({
    mutationFn: () =>
      api.post(`/purchase-orders/${order.id}/revise`, {
        reason: reason.trim(),
        ...comercialBody(comercial),
        items: itensBody(linhas),
      }),
    onSuccess: onSaved,
    onError: (e) => {
      setErro(errorMessage(e));
      setErros(fieldErrors(e));
    },
  });

  const itens = linhas.reduce((s, l) => s + totalDaLinha(l), 0);
  const total = itens + (comercial.freightCents ?? 0) - (comercial.discountCents ?? 0);
  const estoura = order.approvedUpToCents !== null && total > order.approvedUpToCents;

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Alterar ${order.code}`}
      description="O pedido já foi emitido. A alteração guarda o valor anterior, quem mudou e por quê."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => salvar.mutate()}
            disabled={reason.trim().length < 10 || salvar.isPending}
          >
            Salvar alteração
          </Button>
        </>
      }
    >
      <FormSection title="Motivo">
        <Field
          label="Por que este pedido está mudando"
          error={erros.reason}
          hint="Mínimo 10 caracteres. Vai para a auditoria e para o histórico do pedido."
        >
          <textarea className="input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </FormSection>

      {estoura && (
        <div style={{ background: T.amberTint, borderRadius: 8, padding: 12, fontSize: 12, lineHeight: 1.6 }}>
          O novo total ({formatCents(total)}) passa da alçada que aprovou este pedido (
          {formatCents(order.approvedUpToCents!)}). Ao salvar, ele <strong>volta para aprovação</strong>.
        </div>
      )}

      <CamposComerciais
        value={comercial}
        onChange={(patch) => setComercial((c) => ({ ...c, ...patch }))}
        erros={erros}
        exigirCentroDeCusto
      />

      <Itens linhas={linhas} onChange={setLinhas} bloqueadas={bloqueadas} />

      <div style={{ textAlign: 'right', fontSize: 13, marginTop: 10 }}>
        <div className="muted">Itens: {formatCents(itens)}</div>
        <div>
          Novo total: <strong>{formatCents(Math.max(total, 0))}</strong>{' '}
          <span className="muted">(era {formatCents(order.totalCents)})</span>
        </div>
      </div>

      {erro && <p style={{ color: T.red, fontSize: 12 }}>{erro}</p>}
    </Drawer>
  );
}
